import { useSetDialogOrgFraming } from '@/hooks/use-dialog';
import type { DialogSession } from '@/types/dialog';

interface OpenerPreviewProps {
  dialog: DialogSession;
  // Whether this dialog's framing snapshot differs from the project's current
  // framing (computed by the parent, which holds the project). Snapshots are
  // per-dialog by design; the note just says which version respondents get.
  snapshotStale: boolean;
}

// Per-dialog opener controls: the org toggle plus a note when this dialog's
// snapshot differs from the current framing. The opener itself is shown and
// edited once, in the framing card above — not repeated per dialog.
export function OpenerPreview({ dialog, snapshotStale }: OpenerPreviewProps) {
  const toggle = useSetDialogOrgFraming();
  // Show the in-flight value while the PATCH settles (the hook keeps
  // isPending true through the refetch, so there's no flicker-back).
  const orgOn =
    toggle.isPending && toggle.variables !== undefined
      ? toggle.variables.personaliseWithOrg
      : dialog.personaliseWithOrg !== false;

  const hasSnapshot = Boolean(dialog.openerBody?.trim());

  return (
    <div className="space-y-2">
      {!hasSnapshot ? (
        <p className="text-sm text-muted-foreground">
          This dialog has no framing snapshot, so respondents get a generic opener. Draft the
          framing above, then create a new dialog.
        </p>
      ) : snapshotStale ? (
        <p className="text-sm text-muted-foreground">
          Created with an older framing. New dialogs use the version above.
        </p>
      ) : null}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={orgOn}
          onChange={(e) =>
            toggle.mutate({ id: dialog.id, projectId: dialog.projectId, personaliseWithOrg: e.target.checked })
          }
        />
        Frame the conversation around the respondent&apos;s organisation
      </label>
      {toggle.isError && (
        <p className="text-sm text-destructive">
          {toggle.error?.message || 'Failed to update. Please try again.'}
        </p>
      )}
    </div>
  );
}
